"use client";

import { motion } from "framer-motion";
import { MapPin, Users, Briefcase } from "lucide-react";
import SectionHero from "../components/SectionHero";
import ContentSection from "../components/ContentSection";
import { useEffect } from "react";

const AuthorisedOffices = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const offices = [
    {
      name: "Dubai Office",
      location: "Dubai, United Arab Emirates",
      description:
        "Coordinating regional shipowners, operators, and supporting documentation, flag, banking and insurance-related formalities.",
      email: "",
      services: [
        "Regional coordination with shipowners and operators",
        "Documentation and flag support",
        "Crew changes and travel logistics",
        "Communication bridge across time zones",
      ],
      highlights: "Crossroads of major trade routes serving Middle East region",
    },
    {
      name: "Thailand Office",
      location: "Bangkok, Thailand",
      description:
        "Closely linked with training, certifications and crew-related activities across Asia-Pacific region.",
      email: "",
      services: [
        "Training program planning and delivery",
        "Practical sessions and specialised courses",
        "Documentation and certification support",
        "Crew recruitment and station support",
      ],
      highlights:
        "Cost-effective base for seafarers attending courses between contracts",
    },
    {
      name: "India Office",
      location: "India",
      description:
        "Supporting the strong pool of seafarers and maritime professionals from India and neighbouring regions.",
      email: "",
      services: [
        "Training and certification pathway guidance",
        "Application and documentation assistance",
        "Interview and assessment coordination",
        "Family support and local contact point",
      ],
      highlights:
        "Connected with extensive pool of qualified maritime professionals",
    },
    {
      name: "Singapore Office",
      location: "Singapore",
      description: "Regional Coordination & Support.",
      email: "",
      services: [
        "Regional coordination and support activities",
        "Operational assistance for maritime processes",
        "Documentation and compliance facilitation",
      ],
      highlights: "Strategic maritime hub connecting major global trade routes",
    },
    {
      name: "Hong Kong Office",
      location: "Hong Kong",
      description: "Training & Certifications.",
      email: "",
      services: [
        "Training and certification guidance",
        "Course coordination and scheduling",
        "Skill enhancement and evaluation support",
      ],
      highlights:
        "Major Asia-Pacific centre for maritime education and compliance",
    },
    {
      name: "Greece Office",
      location: "Greece",
      description: "Seafarer Support & Recruitment.",
      email: "",
      services: [
        "Seafarer recruitment and placement",
        "Documentation and compliance support",
        "Crew assistance and communication facilitation",
      ],
      highlights:
        "One of the world’s largest maritime hubs with strong seafaring heritage",
    },
  ];

  return (
    <div className="pt-20">
      <SectionHero
        title="Authorised Offices"
        subtitle="A Global Footprint with Local Understanding"
        description="Operating through authorised offices in Dubai, Thailand, and Mohali, we enable support across key maritime and training hubs worldwide."
      />

      <ContentSection title="Our Global Presence">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {offices.map((office, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="rounded-2xl bg-slate-50 p-8 shadow-lg"
            >
              <div className="mb-4 flex items-center space-x-3 border-b border-teal-200 pb-4">
                <MapPin className="h-6 w-6 text-teal-600" />
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    {office.name}
                  </h3>
                  <p className="text-sm text-slate-600">{office.location}</p>
                </div>
              </div>

              <p className="mb-6 text-slate-700">{office.description}</p>

              {/* <div className="mb-6 flex items-center space-x-2 rounded-lg bg-teal-50 p-4">
                <Mail className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <a
                  href={`mailto:${office.email}`}
                  className="text-sm font-semibold text-teal-700 hover:text-teal-900 underline"
                >
                  {office.email}
                </a>
              </div> */}

              <div className="mb-6 rounded-lg bg-teal-50 p-4">
                <p className="text-sm font-semibold text-teal-900">
                  {office.highlights}
                </p>
              </div>

              <h4 className="mb-3 font-semibold text-slate-900">Services:</h4>
              <ul className="space-y-2">
                {office.services.map((service, idx) => (
                  <li
                    key={idx}
                    className="flex items-start space-x-2 text-sm text-slate-700"
                  >
                    <Briefcase
                      size={16}
                      className="mt-0.5 flex-shrink-0 text-teal-600"
                    />
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </ContentSection>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-900 sm:text-3xl md:mb-6 md:text-4xl">
              Why Choose Our Offices?
            </h2>
            <p className="mx-auto mb-12 max-w-3xl text-slate-600 md:mb-16">
              Our offices ensure that clients are never too far from a Nautilus
              Times representative who understands their context and can provide
              real-time support.
            </p>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div className="rounded-xl bg-white p-8 text-left shadow-lg">
                <Users className="mb-4 h-8 w-8 text-teal-600" />
                <h3 className="mb-2 text-lg font-bold text-slate-900">
                  Local Expertise
                </h3>
                <p className="text-slate-700">
                  Deep understanding of regional requirements, regulations, and
                  operational practices specific to each location.
                </p>
              </div>
              <div className="rounded-xl bg-white p-8 text-left shadow-lg">
                <Briefcase className="mb-4 h-8 w-8 text-teal-600" />
                <h3 className="mb-2 text-lg font-bold text-slate-900">
                  24/7 Support
                </h3>
                <p className="text-slate-700">
                  Round-the-clock assistance across multiple time zones to
                  ensure seamless support for your maritime operations.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AuthorisedOffices;
